import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, TextField, Stack, Tooltip, IconButton } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LinkIcon from "@mui/icons-material/Link";
import BlockIcon from "@mui/icons-material/Block";
import { useState, useEffect } from "react";
import { createShareLink, revokeShareLink } from "../../api/integrations";
import { db } from "../../firebase";
import { doc, getDoc, updateDoc, deleteField } from "firebase/firestore";

export default function AgentIntegrationsDialog({ open, onClose, agent, tenantId }) {
  const origin = window.location.origin; // or your prod domain
  const embedCode = `<script src="${origin}/embed.js"
  data-tenant-id="${tenantId}"
  data-agent-id="${agent?.id}"
  data-position="bottom-right"
  data-width="380"
  data-height="520"
  async></script>`;

  const iframeCode = `<iframe src="${origin}/chat/${tenantId}/${agent?.id}" width="400" height="600" frameborder="0"></iframe>`;

  const [expiresDays, setExpiresDays] = useState(7);
  const [maxUses, setMaxUses] = useState(0);
  const [shareUrl, setShareUrl] = useState("");
  const [shareJti, setShareJti] = useState("");
  const [shareMeta, setShareMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open || !tenantId || !agent?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const ref = doc(db, "tenants", String(tenantId), "agents", String(agent.id));
        const snap = await getDoc(ref);
        if (!snap.exists()) {
          if (!cancelled) {
            setShareUrl("");
            setShareJti("");
            setShareMeta(null);
          }
          return;
        }
        const meta = snap.data()?.currentShareLink;
        if (!cancelled) {
          if (meta?.url) {
            setShareUrl(meta.url);
            setShareJti(meta.jti || "");
            setShareMeta(meta);
          } else {
            setShareUrl("");
            setShareJti("");
            setShareMeta(null);
          }
        }
      } catch {}
    })();
    return () => {
      cancelled = true;
    };
  }, [open, tenantId, agent?.id]);

  const handleGenerate = async () => {
    if (!tenantId || !agent?.id) return;
    setLoading(true); setErr("");
    try {
      const resp = await createShareLink({ tenantId, agentId: agent.id, expiresInSec: Number(expiresDays) * 24 * 3600, maxUses: Number(maxUses) || 0 });
      const meta = {
        url: resp?.url || "",
        jti: resp?.jti || "",
        expiresAt: resp?.expiresAt || null,
      };
      setShareUrl(meta.url);
      setShareJti(meta.jti);
      setShareMeta(meta);
      const ref = doc(db, "tenants", String(tenantId), "agents", String(agent.id));
      await updateDoc(ref, { currentShareLink: meta });
    } catch (e) {
      setErr(e?.message || "Failed to create link");
    } finally { setLoading(false); }
  };

  const handleCopy = async () => { try { await navigator.clipboard.writeText(shareUrl); } catch {} };
  const handleRevoke = async () => {
    if (!tenantId || !agent?.id || !shareJti) return;
    setLoading(true); setErr("");
    try { await revokeShareLink({ tenantId, agentId: agent.id, jti: shareJti }); setShareUrl(""); setShareJti(""); }
    catch (e) { setErr(e?.message || "Failed to revoke link"); }
    finally { setLoading(false); }
    try {
      const ref = doc(db, "tenants", String(tenantId), "agents", String(agent.id));
      await updateDoc(ref, { currentShareLink: deleteField() });
      setShareMeta(null);
    } catch {}
  };

  const daysLeft = shareMeta?.expiresAt
    ? Math.max(0, Math.ceil((shareMeta.expiresAt * 1000 - Date.now()) / (24 * 3600 * 1000)))
    : null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle
        sx={{
          bgcolor: (theme) => theme.palette.background.paper,
          color: (theme) => theme.palette.text.primary,
          borderBottom: 1,
          borderColor: "divider",
          fontWeight: 700,
          letterSpacing: 0.2,
        }}
      >
        Integrations for {agent?.name}
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          bgcolor: (theme) =>
            theme.palette.mode === "dark" ? "#0b1220" : "#f8fafc",
        }}
      >
        {/* Public preview */}
        <Box
          sx={{
            mb: 3,
            p: 2,
            border: 1,
            borderColor: (theme) =>
              theme.palette.mode === "dark" ? "#243447" : "#dfe3ec",
            borderRadius: 2,
            bgcolor: (theme) =>
              theme.palette.background.paper,
            boxShadow: "0 10px 28px rgba(0,0,0,0.08)",
          }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>Public Preview</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: 1, overflow: 'hidden', bgcolor: 'action.hover', flexShrink: 0 }}>
              {agent?.logoUrl ? (<img src={agent.logoUrl} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />) : null}
            </Box>
            <Typography variant="subtitle1" sx={{ m: 0 }}>{agent?.name || 'AI Agent'}</Typography>
          </Box>
          {agent?.description && (
            <Typography variant="body2" color="text.secondary">{agent.description}</Typography>
          )}
        </Box>
        <Box
          sx={{
            mb: 3,
            p: 2,
            border: 1,
            borderColor: (theme) =>
              theme.palette.mode === "dark" ? "#243447" : "#dfe3ec",
            borderRadius: 2,
            bgcolor: (theme) =>
              theme.palette.background.paper,
            boxShadow: "0 10px 28px rgba(0,0,0,0.08)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6" sx={{ flex: 1 }}>Embed Script</Typography>
            <Tooltip title="Copy">
              <IconButton onClick={async () => { try { await navigator.clipboard.writeText(embedCode); } catch {} }} size="small">
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <TextField fullWidth multiline value={embedCode} sx={{ mt: 1 }} InputProps={{ readOnly: true }} />
        </Box>

        <Box
          sx={{
            mb: 3,
            p: 2,
            border: 1,
            borderColor: (theme) =>
              theme.palette.mode === "dark" ? "#243447" : "#dfe3ec",
            borderRadius: 2,
            bgcolor: (theme) =>
              theme.palette.background.paper,
            boxShadow: "0 10px 28px rgba(0,0,0,0.08)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6" sx={{ flex: 1 }}>Iframe Embed</Typography>
            <Tooltip title="Copy">
              <IconButton onClick={async () => { try { await navigator.clipboard.writeText(iframeCode); } catch {} }} size="small">
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <TextField fullWidth multiline value={iframeCode} sx={{ mt: 1 }} InputProps={{ readOnly: true }} />
        </Box>

        <Box
          sx={{
            mb: 3,
            p: 2,
            border: 1,
            borderColor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(94,129,244,0.4)"
                : "rgba(99,102,241,0.35)",
            borderRadius: 2,
            bgcolor: (theme) =>
              theme.palette.mode === "dark" ? "#111827" : "#f8fafc",
            boxShadow: "0 10px 28px rgba(0,0,0,0.1)",
          }}
        >
          <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LinkIcon /> Shareable Link
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Generate a private link that works even if the agent is not publicly enabled. You can set an expiration and an optional usage cap.
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
            <TextField type="number" size="small" label="Expires (days)" value={expiresDays} onChange={(e) => setExpiresDays(e.target.value)} sx={{ width: 160 }} />
            <TextField type="number" size="small" label="Max uses (0 = unlimited)" value={maxUses} onChange={(e) => setMaxUses(e.target.value)} sx={{ width: 220 }} />
            <Button variant="contained" onClick={handleGenerate} disabled={loading}>Generate</Button>
          </Stack>
          {err && (<Typography color="error" variant="body2" sx={{ mb: 1 }}>{err}</Typography>)}
          {shareUrl && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <TextField fullWidth value={shareUrl} InputProps={{ readOnly: true }} />
                <Tooltip title="Copy"><IconButton onClick={handleCopy}><ContentCopyIcon /></IconButton></Tooltip>
                <Tooltip title="Revoke"><span><IconButton onClick={handleRevoke} disabled={loading}><BlockIcon /></IconButton></span></Tooltip>
              </Stack>
              {daysLeft !== null && (
                <Typography variant="body2" color="text.secondary">
                  {daysLeft} day{daysLeft === 1 ? "" : "s"} remaining
                </Typography>
              )}
            </Box>
          )}
        </Box>

        

        
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
